import { useState, useEffect, useCallback } from "react"
import { ClipboardCheck, Clock, Loader2, Users } from "lucide-react"

import apiClient from "@/lib/api/apiClient"
import { showSuccess, showError, formatApiError } from "@/lib/utils/notifications"
import { isCompanyDuplicateAddressCancelledError } from "@/features/company/utils/companyDuplicateAddress"
import { getFilterValueAngularStyle, mergeAngularFullFormParityFields } from "../../utils/companyValidators"
import { sanitizeCompanyPostPayload } from "../../utils/companyPayloadUtils"
import { useUserRole } from "@/hooks/useUserRole"
import { SectionCard } from "@/components/ui/SectionCard"
import tabStyles from "../../styles/companyInfoTab.module.css"

import { COMPANY_ROLE_NAMES, EXEC_SUMMARY_EDITABLE_ROLES } from "../../constants"
import type { CompanyStaffMember, ExecutiveSummaryTabProps } from "../../types"

function parseStaffListResponse(res: unknown): CompanyStaffMember[] {
    const raw = (res as { data?: unknown })?.data ?? res
    if (Array.isArray(raw)) return raw as CompanyStaffMember[]
    if (raw && typeof raw === "object" && Array.isArray((raw as { data?: unknown }).data)) {
        return (raw as { data: CompanyStaffMember[] }).data
    }
    if (raw && typeof raw === "object" && Array.isArray((raw as { staff?: unknown }).staff)) {
        return (raw as { staff: CompanyStaffMember[] }).staff
    }
    return []
}

function slugForExecSummaryOptionTestId(value: string): string {
    return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "option"
}

export function ExecutiveSummaryTab({ company, companyId: companyIdProp, onUpdate }: ExecutiveSummaryTabProps) {
    const { roleName } = useUserRole()
    const questionNonClickable = !EXEC_SUMMARY_EDITABLE_ROLES.includes(roleName as typeof EXEC_SUMMARY_EDITABLE_ROLES[number])
    const companyId = companyIdProp ?? company?.company_id ?? company?.id

    const [staffList, setStaffList] = useState<CompanyStaffMember[]>([])
    const [staffLoading, setStaffLoading] = useState(false)

    const [formData, setFormData] = useState({
        income_requirement_criteria: String(company?.income_requirement_criteria ?? ""),
        allow_late_rent: String(company?.allow_late_rent ?? ""),
        eviction_credit_report: String(company?.eviction_credit_report ?? ""),
        time_frame_period: String(company?.time_frame_period ?? ""),
        allow_applicant_move: String(company?.allow_applicant_move ?? ""),
        applicant_purchase_policy: String(company?.applicant_purchase_policy ?? ""),
        main_contact_person: String(company?.main_contact_person ?? ""),
        special_note: String(company?.special_note ?? ""),
    })

    const loadStaffs = useCallback(async () => {
        if (!companyId) return
        setStaffLoading(true)
        try {
            const res = await apiClient.get("/api/property_staff", {
                params: { company_id: companyId, sortBy: "desc" },
            })
            const list = parseStaffListResponse(res)
            setStaffList(list)
        } catch {
            setStaffList([])
        } finally {
            setStaffLoading(false)
        }
    }, [companyId])

    useEffect(() => {
        loadStaffs()
    }, [loadStaffs])

    useEffect(() => {
        setFormData({
            income_requirement_criteria: String(company?.income_requirement_criteria ?? ""),
            allow_late_rent: String(company?.allow_late_rent ?? ""),
            eviction_credit_report: String(company?.eviction_credit_report ?? ""),
            time_frame_period: String(company?.time_frame_period ?? ""),
            allow_applicant_move: String(company?.allow_applicant_move ?? ""),
            applicant_purchase_policy: String(company?.applicant_purchase_policy ?? ""),
            main_contact_person: String(company?.main_contact_person ?? ""),
            special_note: String(company?.special_note ?? ""),
        })
    }, [company])



    /** Same as `PropertyExecutiveSummaryTab`: choosing Q3 Yes clears Q4; Q4 uses `inert` unless Q3 is No. */
    const handleChange = (field: string, value: string) => {
        if (field === "eviction_credit_report" && value === "1") {
            setFormData((prev) => ({ ...prev, eviction_credit_report: value, time_frame_period: "" }))
        } else {
            setFormData((prev) => ({ ...prev, [field]: value }))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        try {
            /**
             * Angular `editCompany()` posts the full `companyEditForm` (getFilterValue).
             * Start from sanitized GET company scalars, overlay this tab’s fields, then parity keys.
             */
            const companyFlat = sanitizeCompanyPostPayload(
                company && typeof company === "object"
                    ? { ...(company as Record<string, unknown>) }
                    : {},
            )
            const eviction = String(formData.eviction_credit_report ?? "").trim()
            let timeFrame = String(formData.time_frame_period ?? "").trim()
            if (eviction === "1") timeFrame = ""
            const raw: Record<string, unknown> = {
                ...companyFlat,
                income_requirement_criteria: String(formData.income_requirement_criteria ?? "").trim(),
                allow_late_rent: String(formData.allow_late_rent ?? "").trim(),
                eviction_credit_report: eviction,
                time_frame_period: timeFrame,
                allow_applicant_move: String(formData.allow_applicant_move ?? "").trim(),
                applicant_purchase_policy: String(formData.applicant_purchase_policy ?? "").trim(),
                main_contact_person: String(formData.main_contact_person ?? "").trim(),
                special_note: String(formData.special_note ?? ""),
            }
            const filtered = getFilterValueAngularStyle(raw) as Record<string, unknown>
            const payload = mergeAngularFullFormParityFields(company as Record<string, unknown> | undefined, filtered)

            await onUpdate(payload)
            showSuccess("Company Updated  Successfully!")
        } catch (err: unknown) {
            if (isCompanyDuplicateAddressCancelledError(err)) return
            showError(formatApiError(err))
        }
    }



    const ButtonGroup = ({
        name,
        value,
        options,
        disabled,
        questionId,
    }: {
        name: string
        value: string
        options: { label: string; value: string }[]
        disabled?: boolean
        /** Stable prefix for `data-testid`, e.g. `q1-income-requirement`. */
        questionId: string
    }) => (
        <div className="flex flex-wrap gap-2">
            {options.map((opt) => {
                const isSelected = String(value) === String(opt.value)
                return (
                    <button
                        key={opt.value}
                        type="button"
                        data-testid={`company-exec-summary-${questionId}-${slugForExecSummaryOptionTestId(opt.value)}`}
                        disabled={disabled}
                        aria-pressed={isSelected}
                        onClick={() => !disabled && handleChange(name, opt.value)}
                        className={`inline-flex min-h-[40px] min-w-[72px] items-center justify-center rounded-md border px-[22px] py-2 text-sm font-semibold transition-colors ${
                            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                        } ${
                            isSelected
                                ? "border-[rgb(21_151_255)] bg-[rgb(21_151_255)] text-white shadow-[0_1px_3px_rgba(21,151,255,0.35)]"
                                : "border-[#cbd5e1] bg-white text-[#334155] hover:border-[#94a3b8] hover:bg-[#f1f5f9]"
                        }`}
                    >
                        {opt.label}
                    </button>
                )
            })}
        </div>
    )

    const questionClass = "text-sm font-medium text-slate-800 leading-relaxed max-w-4xl"
    const questionBlockClass = "space-y-3"

    return (
        <form
            data-testid="company-executive-summary-tab-form"
            onSubmit={handleSubmit}
            className={`${tabStyles.tabContent} animate-in fade-in duration-300`}
            data-tab-id="executivesummary"
        >
            <SectionCard
                icon={ClipboardCheck}
                title="Applicant Eligibility"
                testId="company-exec-summary-section-applicant-eligibility"
            >
                <div className="space-y-6">
                    <section className={questionBlockClass}>
                        <p className={questionClass}>
                            1. Does the PMC/Community send applicants to Liberty Rent that do not meet their income requirement?
                        </p>
                        <ButtonGroup
                            name="income_requirement_criteria"
                            value={formData.income_requirement_criteria}
                            options={[{ label: "Yes", value: "1" }, { label: "No", value: "0" }]}
                            disabled={questionNonClickable}
                            questionId="q1-income-requirement"
                        />
                    </section>

                    <section className={questionBlockClass}>
                        <p className={questionClass}>
                            2. Does the PMC/Community allow for a current LR or non LR resident, that has been late on their rent multiple times, to reapply/apply with Liberty Rent at renewal?
                        </p>
                        <ButtonGroup
                            name="allow_late_rent"
                            value={formData.allow_late_rent}
                            options={[{ label: "Yes", value: "1" }, { label: "No", value: "0" }]}
                            disabled={questionNonClickable}
                            questionId="q2-late-rent"
                        />
                    </section>

                    <section className={questionBlockClass}>
                        <p className={questionClass}>
                            3. Does the PMC/Community allow applicants with an eviction on their credit report to move in the apartment home?
                        </p>
                        <ButtonGroup
                            name="eviction_credit_report"
                            value={formData.eviction_credit_report}
                            options={[{ label: "Yes", value: "1" }, { label: "No", value: "0" }]}
                            disabled={questionNonClickable}
                            questionId="q3-eviction-credit"
                        />
                    </section>

                    <section
                        className={questionBlockClass}
                        inert={formData.eviction_credit_report !== "0" ? true : undefined}
                    >
                        <p className={questionClass}>
                            4. If the answer is &apos;No&apos; to question 3, what is the time frame the PMC/Community will consider?
                        </p>
                        <ButtonGroup
                            name="time_frame_period"
                            value={formData.time_frame_period}
                            options={[
                                { label: "After 2 yrs", value: "After 2 yrs" },
                                { label: "After 3 yrs", value: "After 3 yrs" },
                            ]}
                            disabled={questionNonClickable}
                            questionId="q4-time-frame"
                        />
                    </section>

                    <section className={questionBlockClass}>
                        <p className={questionClass}>
                            5. Does the PMC/Community allow an applicant to move in with a rent debt?
                        </p>
                        <ButtonGroup
                            name="allow_applicant_move"
                            value={formData.allow_applicant_move}
                            options={[{ label: "Yes", value: "1" }, { label: "No", value: "0" }]}
                            disabled={questionNonClickable}
                            questionId="q5-rent-debt-move"
                        />
                    </section>
                </div>
            </SectionCard>

            <SectionCard
                icon={Clock}
                title="Unit Hold Policy"
                testId="company-exec-summary-section-unit-hold-policy"
            >
                <section className={questionBlockClass}>
                        <p className={questionClass}>
                            6. How long will the PMC/community hold the unit once a Liberty Rent Applicant is approved?
                        </p>
                        <ButtonGroup
                            name="applicant_purchase_policy"
                            value={formData.applicant_purchase_policy}
                            options={[
                                { label: "48 hrs", value: "48 hrs" },
                                { label: "72 hrs", value: "72 hrs" },
                                { label: "One Week", value: "One Week" },
                            ]}
                            disabled={questionNonClickable}
                            questionId="q6-hold-unit"
                        />
                </section>
            </SectionCard>

            <SectionCard
                icon={Users}
                title="Program Management"
                testId="company-exec-summary-section-program-management"
            >
                <div className="space-y-6">
                    <section className={questionBlockClass}>
                        <p className={questionClass}>
                            7. Who is the main point of contact(s) [person(s) who helps manage the program]?
                        </p>
                        {staffLoading ? (
                            <div
                                data-testid="company-exec-summary-q7-staff-loading"
                                className="flex items-center gap-2 text-sm text-slate-500"
                            >
                                <Loader2 className="h-4 w-4 animate-spin text-[rgb(21_151_255)]" aria-hidden />
                                Loading users…
                            </div>
                        ) : (
                            <ul className="m-0 list-none space-y-2 p-0" data-testid="company-exec-summary-q7-staff-list">
                                {staffList.map((staff, selectedIndex) => {
                                    // Angular: `CompanyRoleNames[staff.role_name]` only (blank if unmapped)
                                    const roleLabel =
                                        staff?.role_name && COMPANY_ROLE_NAMES[staff.role_name]
                                            ? COMPANY_ROLE_NAMES[staff.role_name]
                                            : ""
                                    const roleId = staff?.role_id != null ? String(staff.role_id) : ""
                                    const isChecked = formData.main_contact_person === roleId
                                    return (
                                        <li
                                            key={staff?.role_id ?? staff?.user_id ?? selectedIndex}
                                            className={`flex items-start gap-3 ${
                                                questionNonClickable ? "pointer-events-none opacity-60" : ""
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                id={`main_contact_person_${selectedIndex}`}
                                                data-testid={`company-exec-summary-q7-main-contact-${roleId || `idx-${selectedIndex}`}`}
                                                name="main_contact_person"
                                                value={roleId}
                                                checked={isChecked}
                                                disabled={questionNonClickable}
                                                onChange={() =>
                                                    !questionNonClickable && roleId && handleChange("main_contact_person", roleId)
                                                }
                                                className={`mt-1 h-4 w-4 shrink-0 accent-[rgb(21_151_255)] ${
                                                    questionNonClickable ? "cursor-not-allowed" : "cursor-pointer"
                                                }`}
                                            />
                                            <label
                                                htmlFor={`main_contact_person_${selectedIndex}`}
                                                className={`m-0 text-sm leading-snug text-slate-800 ${
                                                    questionNonClickable ? "cursor-default" : "cursor-pointer"
                                                }`}
                                            >
                                                {staff?.name} ({roleLabel})
                                            </label>
                                        </li>
                                    )
                                })}
                            </ul>
                        )}
                    </section>

                    <section className={`${questionBlockClass} pb-1`}>
                        <p className={questionClass}>
                            8. What other parameters does the PMC/Community have that Liberty Rent need to be aware of?
                        </p>
                        <textarea
                            data-testid="company-exec-summary-q8-special-note"
                            rows={5}
                            value={formData.special_note}
                            onChange={(e) => handleChange("special_note", e.target.value)}
                            disabled={questionNonClickable}
                            className="w-full max-w-2xl resize-y rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-60"
                        />
                    </section>
                </div>
            </SectionCard>

            <button
                type="submit"
                data-testid="company-exec-summary-hidden-submit"
                aria-hidden
                className="pointer-events-none absolute h-0 w-0 opacity-0"
                tabIndex={-1}
            />
        </form>
    )
}
